import React, { FC, useEffect, useMemo, useState } from "react";
import { Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import { GridColumnVisibilityModel, GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../../../common/routesUtils";
import { SearchBar } from "../../../../../../../components/common/SearchBar";
import ServerTable from "../../../../../../../components/common/table/ServerTable";
import { useServerTableController } from "../../../../../../../components/common/table/ServerTable/ServerDataGrid/useServerTableController";
import { CustomColumnMenu } from "../../../../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../../../../components/common/table/TableData/TableDataFooter";
import { useCustomTableDataStyles } from "../../../../../../../components/common/table/TableData/useCustomTableDataStyles";
import { PageType } from "../../../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../../../../context/PageConfigrations/usePageConfigurationState";
import {
	EntitiesListDTO,
	EntityItemDTO,
	ItemsListPayload,
} from "../../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../../../hooks/useResource";
import { mapDataColumns, mapDataRows } from "../../../../../../../utilities/dataGridUtilities";
import CustomNoRowsOverlay from "../../../components/CustomNoRowsOverlay";

interface Props {
	transportId: string | number;
	descriptionTrip: string;
	visibleEntities: string[];
	hideEntities?: (list: string[]) => void;
	showEntities?: (list: string[]) => void;
}

export const TripEntities: FC<Props> = ({
	transportId,
	hideEntities,
	showEntities,
	descriptionTrip,
	visibleEntities,
}) => {
	const theme = useTheme();
	const { t } = useTranslation(["feature.results", "result.transport", "common"]);
	const tableSx = useCustomTableDataStyles();
	const { appliedFilters } = useFiltersRegistry();
	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();
	const [search, setSearch] = useState("");

	const [config, setConfig] = usePageConfigurationState(PageType.ResultsTransportGreenRouter, {
		emissionsKey: "co2Ttw",
		entityEmissionsKey: "co2Ttw",
		page: 0,
		entityPage: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},
		entitySortField: undefined,
		entitySortDirection: undefined,
		entityColumnVisibilityModel: {},
	});

	// generic table controller
	const {
		paginationModel,
		handlePaginationChange,
		sortModel,
		handleSortModelChange,
		columnVisibilityModel,
		handleColumnVisibilityChange,
	} = useServerTableController({
		config,
		setConfig,
	});

	const { data, isLoading, clearCache, reload } = useReadResource<ItemsListPayload, EntitiesListDTO>(
		{
			route: ApiRoutes.ResultsTransportEntitiesGreenRouter,
			path: { id: transportId },
			query: {
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				sortBy: sortModel[0]?.field,
				sortDir: sortModel[0]?.sort ?? "asc",
			},
		},
		{ readMethod: "post" },
		{ ...appliedFilters.payload, search }
	);

	const updateSearch = (value: string) => {
		clearCache();
		setSearch(value);
	};

	const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>(() => ({
		type: "include",
		ids: new Set<GridRowId>(visibleEntities),
	}));

	useEffect(() => {
		setSelectedRowIds({
			type: "include",
			ids: new Set<GridRowId>(visibleEntities),
		});
	}, [visibleEntities]);

	const handleRowSelection = (newSelection: GridRowSelectionModel) => {
		setSelectedRowIds({
			type: newSelection.type,
			ids: new Set(newSelection.ids),
		});
	};

	const selectedRowsId = useMemo<GridRowId[]>(() => {
		if (selectedRowIds.type !== "include") {
			return [];
		}

		return Array.from(selectedRowIds.ids);
	}, [selectedRowIds]);

	useEffect(() => {
		reload();
	}, []);

	const navigateToEntity = (row: EntityItemDTO) => {
		visitPage(PageType.ResultsTransportGreenRouterDetail, false, row.description, row.id);
		navigate(
			AppRoutes.ResultsEntityGreenRouterDetail.replace(":entityId", row.id).replace(
				":transportId",
				row.transportId
			)
		);
	};

	const rows = mapDataRows<EntityItemDTO>(data, "id");
	const columns = mapDataColumns<EntityItemDTO>(
		data,
		t,
		theme,
		[
			{ field: "description", headerName: "description", renderAs: "text" },
			{ field: "originDescription", headerName: "departure", renderAs: "text" },
			{ field: "destinationDescription", headerName: "arrival", renderAs: "text" },
			{ field: "distance", headerName: "distance", renderAs: "measure" },
			{ field: "weight", headerName: "weight", renderAs: "measure" },
			{ field: "emissionWtw", headerName: "emissions", renderAs: "measure" },
			{ field: "emissionTtw", headerName: "emissions", renderAs: "measure" },
			{ field: "date", headerName: "date", renderAs: "date" },
		],
		navigateToEntity
	);

	const defaultColumnVisibility: GridColumnVisibilityModel = useMemo(() => {
		return columns.reduce((acc, col) => {
			if (col.field === "emissionWtw") {
				acc[col.field] = config.emissionsKey === "co2Ttw";
				return acc;
			}

			if (col.field === "emissionTtw") {
				acc[col.field] = config.emissionsKey === "co2Wtw";
				return acc;
			}

			acc[col.field] = true;
			return acc;
		}, {} as GridColumnVisibilityModel);
	}, [config.emissionsKey]);

	const mergedVisibilityModel = useMemo(
		() => ({
			...defaultColumnVisibility,
			...columnVisibilityModel,
		}),
		[defaultColumnVisibility, columnVisibilityModel]
	);

	return (
		<Stack sx={{ marginX: 2, marginY: 10 }}>
			<Typography variant="h5" color="textPrimary" mb={1}>
				{t("feature.results:tripDetails/transport entities", { id: descriptionTrip })}
			</Typography>

			<Divider />

			<Stack direction="row" spacing={2} marginTop={2} marginBottom={2}>
				{selectedRowsId.length > 0 && (
					<Stack direction="row" spacing={1}>
						<Button
							variant="contained"
							sx={{ whiteSpace: "nowrap" }}
							onClick={() => showEntities?.(selectedRowsId.map((v) => v.toString()))}
							disabled={isLoading || selectedRowsId.length === 0}
						>
							{t("feature.results:tripDetails/show entity on arc")}
						</Button>

						<Button
							variant="outlined"
							sx={{ whiteSpace: "nowrap" }}
							onClick={() => hideEntities?.(selectedRowsId.map((v) => v.toString()))}
							disabled={isLoading || selectedRowsId.length === 0}
						>
							{t("feature.results:tripDetails/hide entity on arc")}
						</Button>
					</Stack>
				)}

				<SearchBar
					onChange={updateSearch}
					placeholder={t("feature.results:tripDetails/search entities placeholder")}
				/>
			</Stack>

			<ServerTable
				isLoading={isLoading}
				getRowId={(row) => row.id}
				rows={rows}
				columns={columns}
				rowHeight={56}
				checkboxSelection
				rowSelectionModel={selectedRowIds}
				onRowSelectionModelChange={handleRowSelection}
				paginationModel={paginationModel}
				onPaginationModelChange={handlePaginationChange}
				rowCount={data?.itemsCount ?? 0}
				pageSizeOptions={[10, 25, 50]}
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				columnVisibilityModel={mergedVisibilityModel}
				onColumnVisibilityModelChange={handleColumnVisibilityChange}
				keepNonExistentRowsSelected
				slots={{
					noRowsOverlay: () => <CustomNoRowsOverlay />,
					footer: CustomDataGridFooter,
					columnMenu: CustomColumnMenu,
				}}
				sx={tableSx}
			/>
		</Stack>
	);
};
